/* eslint-disable react/require-default-props */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Loader2, Newspaper, RefreshCw, Search } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import AppLayout from '../../../layouts/AppLayout';
import ArticleCard from './components/ArticleCard';
import SourceFilter from './components/SourceFilter';
import { fetchAllFeeds, RSS_FEEDS } from './utils/rssParser';

function Reader({ title }) {
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeSource, setActiveSource] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);

  const loadArticles = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const fetchedArticles = await fetchAllFeeds();
      setArticles(fetchedArticles);
    } catch (err) {
      console.error('Failed to fetch articles:', err);
      setError('Failed to load articles. Please try again.');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  const handleRefresh = () => {
    loadArticles(true);
  };

  const filteredArticles = useMemo(() => {
    let filtered = articles;

    // Filter by source
    if (activeSource !== 'all') {
      filtered = filtered.filter((article) => article.source === activeSource);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (article) =>
          article.title.toLowerCase().includes(query) ||
          article.description?.toLowerCase().includes(query),
      );
    }

    return filtered;
  }, [articles, activeSource, searchQuery]);

  // Group articles by source for stats
  const sourceStats = useMemo(() => {
    const stats = {};
    articles.forEach((article) => {
      stats[article.source] = (stats[article.source] || 0) + 1;
    });
    return stats;
  }, [articles]);

  if (isLoading) {
    return (
      <AppLayout title={title}>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading articles from RSS feeds...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={title}>
      <div className="h-full overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header Card */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Newspaper className="h-5 w-5 text-primary" />
                    Tech News Reader
                  </CardTitle>
                  <Badge variant="secondary">{articles.length} articles</Badge>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                  style={{ border: '1px solid hsl(var(--border))' }}
                />
              </div>

              {/* Source Filter */}
              <SourceFilter
                sources={RSS_FEEDS}
                activeSource={activeSource}
                onSourceChange={setActiveSource}
              />

              {/* Source Stats */}
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {RSS_FEEDS.map((source) => (
                  <div
                    key={source.name}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground"
                  >
                    <span>{source.icon}</span>
                    <span>{source.name}:</span>
                    <span className="font-medium text-foreground">
                      {sourceStats[source.name] || 0}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Error State */}
          {error && (
            <Card className="mb-6 border-red-200 bg-red-50">
              <CardContent className="py-4">
                <p className="text-red-600 text-sm">{error}</p>
              </CardContent>
            </Card>
          )}

          {/* Articles Grid */}
          {filteredArticles.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="flex flex-col items-center justify-center text-center">
                  <Newspaper className="h-12 w-12 text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">
                    {searchQuery
                      ? 'No articles found matching your search.'
                      : 'No articles available from this source.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

Reader.propTypes = {
  title: PropTypes.string.isRequired,
};

export default Reader;
